'use client';

/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';

/**
 * Internal dependencies
 */
import { cn } from '@Utils/cn';
import {
  Activity,
  Blocks,
  BotMessageSquare,
  // Telescope, // re-enable with the Omni Search nav item below
  BrainCircuit,
  CalendarDays,
  ClipboardCheck,
  FolderOpen,
  HandPlatter,
  Handshake,
  Waves,
  Hash,
  Headset,
  Inbox,
  Mailbox,
  Phone,
  Layers,
  SlidersHorizontal,
  Menu,
  MessageCircle,
  MessageCircleDashed,
  Percent,
  Receipt,
  Route,
  ScrollText,
  SearchCode,
  ShieldUser,
  ShoppingCart,
  SquareDashed,
  Store,
  Tags,
  TextSelect,
  Ticket,
  ToyBrick,
  Users,
  UserRound,
  UserStar,
  Zap,
  Calendar,
  Clock,
  Image,
  Database,
  LayoutDashboard,
  LayoutTemplate,
  Boxes,
  SquareLibrary,
  BringToFront,
  ChartNoAxesCombined,
} from 'lucide-react';
import { RefObject, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isActivePath } from '../../utils';
import NavGroup from './NavGroup';
import NavItem from './NavItem';
import ShowFor from '@Components/ShowFor';
import { useAuth } from '../../contexts/auth';

// Permissions that grant a non-admin user access to individual Admin-section
// items. The section header shows when the user holds any of these, while
// role-only items inside the section stay admin-only.
const ADMIN_GROUP_PERMISSIONS = [
  'FLOW_BUILDER_READ',
  'FLOW_BUILDER_WRITE',
  'FLOW_WRITE',
  'ACCESS_OMNI_AGENT',
  'DASHBOARD_READ',
  'PROMO_CODES_READ',
  'PROMO_CODES_WRITE',
  'REGISTRATION_CODES_READ',
  'REGISTRATION_CODES_WRITE',
  'NUMBER_READ',
  'INBOX_MANAGE',
];

const noiseStyle = {
  backgroundImage: `url("data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='noiseFilter'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23noiseFilter)' opacity='0.1'/></svg>")`,
  backgroundRepeat: 'repeat',
  backgroundSize: '220px 220px',
};

export default function Sidebar({
  scrollContainerRef,
  noMenuButton = false,
}: {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  noMenuButton?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showMenuButton, setShowMenuButton] = useState(true);
  const { pathname } = useLocation();
  const { checkAccess } = useAuth();

  // Admins see the whole section; everyone else sees it as soon as any one of
  // its permission-gated items applies to them (checkAccess ORs the two lists).
  const showAdminGroup = checkAccess(['ADMIN_ROLE'], ADMIN_GROUP_PERMISSIONS);

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return;

    const handleScroll = () => {
      // Only hide after scrolling at least 20px
      setShowMenuButton(el.scrollTop < 10);
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [scrollContainerRef]);

  return (
    <div className="relative">
      {noMenuButton ? null : (
        <div
          className={cn(
            'absolute top-0 -right-18 z-10 mt-4 px-2 transition-opacity duration-100',
            showMenuButton
              ? 'pointer-events-auto opacity-100'
              : 'pointer-events-none opacity-0',
          )}
        >
          <Button
            onClick={() => setIsExpanded((prev) => !prev)}
            className="p-2 text-zinc-400 transition-colors hover:text-zinc-900"
            size="icon"
            variant="outline"
          >
            <Menu size={20} />
          </Button>
        </div>
      )}

      <aside
        className={cn(
          'relative flex h-full shrink-0 flex-col overflow-x-hidden overflow-y-auto bg-zinc-900 text-white transition-[width] duration-300 ease-in-out [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-700',
          isExpanded ? 'w-[220px]' : 'w-14',
        )}
        style={{ ...noiseStyle }}
      >
        <header className="pt-4">
          <Link to="/" className="group overflow-hidden text-nowrap">
            <div
              className={cn(
                'relative transition-all duration-200',
                isExpanded ? 'h-16' : 'h-8',
              )}
            >
              <img
                src="/one-point-logo-horizontal.webp"
                alt="One Point"
                className={cn(
                  'absolute left-0 w-[220px] max-w-none px-4 transition-all duration-200',
                  isExpanded ? 'opacity-100' : 'opacity-0',
                )}
              />
              <img
                src="/one-point-mark.webp"
                alt="One Point"
                className={cn(
                  'mx-3 w-8 max-w-none pt-1 transition-all duration-300',
                  !isExpanded ? 'opacity-80 hover:opacity-100' : 'opacity-0',
                )}
              />
            </div>
          </Link>
        </header>
        <nav className="flex flex-col gap-3 p-4">
          <NavGroup title="Community" menuExpanded={isExpanded}>
            <ShowFor permissions={['ADMIN_ACCESS_ALL_ORGS']}>
              <NavItem
                to="/command-center"
                icon={<ChartNoAxesCombined className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/command-center')}
              >
                Command Center
              </NavItem>
            </ShowFor>

            <ShowFor permissions={['FLOW_READ']}>
              <NavItem
                to="/flows/list"
                icon={<Waves className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/flows/list')}
              >
                Flows
              </NavItem>
            </ShowFor>

            <ShowFor permissions={['MEMBERS_READ']}>
              <NavItem
                to="/members"
                icon={<UserStar className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/members')}
              >
                Club Members
              </NavItem>
            </ShowFor>

            <ShowFor permissions={['INSIGHTS_READ']}>
              <NavItem
                to="/insights"
                icon={<Activity className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/insights')}
              >
                Insights
              </NavItem>
            </ShowFor>

            {/* <NavItem
              to="/omni-search"
              icon={<Telescope className="size-5" />}
              menuExpanded={isExpanded}
              active={isActivePath(pathname, '/omni-search')}
            >
              Omni Search
            </NavItem> */}

            {/* <NavItem
              to="/fish"
              icon={<IdCard className="size-5" />}
              menuExpanded={isExpanded}
              active={isActivePath(pathname, '/fish')}
            >
              Team Members
            </NavItem> */}

            <ShowFor permissions={['GUESTS_READ']}>
              <NavItem
                to="/guests"
                icon={<Users className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/guests')}
              >
                Guests
              </NavItem>
            </ShowFor>

            <ShowFor roles={['ADMIN_ROLE']} permissions={['TEAM_MEMBERS_READ']}>
              <NavItem
                to="/team-members"
                icon={<Users className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/team-members')}
              >
                Team Members
              </NavItem>
            </ShowFor>
            {/* <NavItem
              to="/fish"
              icon={<HandHeart className="size-5" />}
              menuExpanded={isExpanded}
              active={isActivePath(pathname, '/fish')}
            >
              Extended Family
            </NavItem> */}
            <ShowFor permissions={['TAGS_READ']}>
              <NavItem
                to="/tags"
                icon={<Tags className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/tags')}
              >
                Tags
              </NavItem>
            </ShowFor>
            <ShowFor permissions={['SEGMENTS_READ']}>
              <NavItem
                to="/segments"
                icon={<Blocks className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/segments')}
              >
                Segments
              </NavItem>
            </ShowFor>
          </NavGroup>

          <ShowFor
            permissions={[
              'INBOX_READ',
              'CAMPAIGNS_READ',
              'MESSAGE_TEMPLATES_READ',
            ]}
          >
            <NavGroup title="Engage" menuExpanded={isExpanded}>
              <ShowFor permissions={['INBOX_READ']}>
                <NavItem
                  to="/inbox"
                  icon={<Inbox className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/inbox')}
                >
                  Inbox
                </NavItem>
              </ShowFor>
              <ShowFor permissions={['CAMPAIGNS_READ']}>
                <NavItem
                  to="/campaigns"
                  icon={<MessageCircle className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/campaigns')}
                >
                  Campaigns
                </NavItem>
              </ShowFor>
              <ShowFor permissions={['MESSAGE_TEMPLATES_READ']}>
                <NavItem
                  to="/message-templates"
                  icon={<MessageCircleDashed className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/message-templates')}
                >
                  Message Templates
                </NavItem>
              </ShowFor>
            </NavGroup>
          </ShowFor>

          <ShowFor permissions={['EVENTS_READ']}>
            <NavGroup title="Events" menuExpanded={isExpanded}>
              <NavItem
                to="/events/calendar"
                icon={<Calendar className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/events/calendar')}
              >
                Calendar
              </NavItem>
              <NavItem
                to="/events/general"
                icon={<CalendarDays className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/events/general')}
              >
                General
              </NavItem>
              <NavItem
                to="/ticketed-events"
                icon={<Ticket className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/ticketed-events')}
              >
                Ticketed Events
              </NavItem>
              <NavItem
                to="/events/tickets"
                icon={<Receipt className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/events/tickets')}
              >
                Tickets
              </NavItem>
              <NavItem
                to="/private-dining-events"
                icon={<HandPlatter className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/private-dining-events')}
              >
                Private Dining
              </NavItem>
              <NavItem
                to="/events/categories"
                icon={<Layers className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/events/categories')}
              >
                Categories
              </NavItem>
              <NavItem
                to="/events/tags"
                icon={<Tags className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/events/tags')}
              >
                Tags
              </NavItem>
              <NavItem
                to="/events/organizers"
                icon={<Users className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/events/organizers')}
              >
                Organizers
              </NavItem>
            </NavGroup>
          </ShowFor>

          <ShowFor permissions={['VENUES_READ', 'SPACES_READ']}>
            <NavGroup title="Locations" menuExpanded={isExpanded}>
              <ShowFor permissions={['VENUES_READ']}>
                <NavItem
                  to="/venues"
                  icon={<Store className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/venues')}
                >
                  Venues
                </NavItem>
              </ShowFor>
              <ShowFor permissions={['SPACES_READ']}>
                <NavItem
                  to="/spaces"
                  icon={<SquareDashed className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/spaces')}
                >
                  Spaces
                </NavItem>
              </ShowFor>
            </NavGroup>
          </ShowFor>

          <ShowFor permissions={['SHOP_READ']}>
            <NavGroup title="Shop" menuExpanded={isExpanded}>
              <NavItem
                to="/shop/orders"
                icon={<ShoppingCart className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/shop/orders')}
              >
                Orders
              </NavItem>
            </NavGroup>
          </ShowFor>

          <ShowFor permissions={['CONTENT_READ']}>
            <NavGroup title="Content" menuExpanded={isExpanded}>
              <NavItem
                to="/content-editor"
                icon={<LayoutTemplate className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/content-editor')}
              >
                Content Editor
              </NavItem>
            </NavGroup>
          </ShowFor>

          <ShowFor permissions={['AI_ACCESS']}>
            <NavGroup title="AI" menuExpanded={isExpanded}>
              <NavItem
                to="/ai-chat"
                icon={<BotMessageSquare className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/ai-chat')}
              >
                Chat
              </NavItem>
              <NavItem
                to="/agents"
                icon={<BrainCircuit className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/agents')}
              >
                Agents
              </NavItem>
              <NavItem
                to="/evals"
                icon={<ClipboardCheck className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/evals')}
              >
                Evals
              </NavItem>
            </NavGroup>
          </ShowFor>

          {showAdminGroup && (
            <NavGroup title="Admin" menuExpanded={isExpanded}>
              <ShowFor roles={['ADMIN_ROLE']}>
                <NavItem
                  to="/trigger-manager"
                  icon={<Zap className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/trigger-manager')}
                >
                  Trigger Manager
                </NavItem>
              </ShowFor>

              <ShowFor permissions={['FLOW_BUILDER_READ', 'FLOW_BUILDER_WRITE']}>
                <NavItem
                  to="/flows/builder"
                  icon={<ToyBrick className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/flows/builder')}
                >
                  Flow Builder
                </NavItem>
              </ShowFor>

              <ShowFor permissions={['FLOW_WRITE']}>
                <NavItem
                  to="/flows/categories"
                  icon={<SquareLibrary className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/flows/categories')}
                >
                  Flow Categories
                </NavItem>
              </ShowFor>

              <ShowFor permissions={['FLOW_WRITE']}>
                <NavItem
                  to="/flows/cards"
                  icon={<BringToFront className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/flows/cards')}
                >
                  Category Cards
                </NavItem>
              </ShowFor>

              <ShowFor roles={['ADMIN_ROLE']}>
                <NavItem
                  to="/flow-manager"
                  icon={<Route className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/flow-manager')}
                >
                  Flow Manager
                </NavItem>
                <NavItem
                  to="/custom-form-builder"
                  icon={<TextSelect className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/custom-form-builder')}
                >
                  Custom Forms
                </NavItem>
              </ShowFor>

              <ShowFor permissions={['ACCESS_OMNI_AGENT']}>
                <NavItem
                  to="/omni-agent"
                  icon={<BotMessageSquare className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/omni-agent')}
                >
                  OnePoint Insights
                </NavItem>
              </ShowFor>

              <ShowFor permissions={['DASHBOARD_READ']}>
                <NavItem
                  to="/dashboards"
                  icon={<LayoutDashboard className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/dashboards')}
                >
                  Dashboards
                </NavItem>

                <NavItem
                  to="/workspaces/manager"
                  icon={<Boxes className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/workspaces/manager')}
                >
                  Workspace Manager
                </NavItem>

                <NavItem
                  to="/workspaces/cards"
                  icon={<BringToFront className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/workspaces/cards')}
                >
                  Workspace Cards
                </NavItem>
              </ShowFor>

              <ShowFor roles={['ADMIN_ROLE']}>
                <NavItem
                  to="/users"
                  icon={<UserRound className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/users')}
                >
                  Users
                </NavItem>
                <NavItem
                  to="/user-roles"
                  icon={<ShieldUser className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/user-roles')}
                >
                  User Roles
                </NavItem>
                <NavItem
                  to="/purchases"
                  icon={<Receipt className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/purchases')}
                >
                  Purchases
                </NavItem>
                <NavItem
                  to="/subscription-tiers"
                  icon={<ScrollText className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/subscription-tiers')}
                >
                  Subscription Tiers
                </NavItem>
              </ShowFor>

              <ShowFor
                permissions={['REGISTRATION_CODES_READ', 'REGISTRATION_CODES_WRITE']}
              >
                <NavItem
                  to="/invite-codes"
                  icon={<SearchCode />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/invite-codes')}
                >
                  Invite Codes
                </NavItem>
                <NavItem
                  to="/partner-referral-codes"
                  icon={<Handshake className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/partner-referral-codes')}
                >
                  Partner Codes
                </NavItem>
              </ShowFor>

              <ShowFor permissions={['PROMO_CODES_READ', 'PROMO_CODES_WRITE']}>
                <NavItem
                  to="/promo-codes"
                  icon={<Percent className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/promo-codes')}
                >
                  Promo Codes
                </NavItem>
              </ShowFor>

              <ShowFor roles={['ADMIN_ROLE']}>
                <NavItem
                  to="/job-runs"
                  icon={<Clock className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/job-runs')}
                >
                  Job Runs
                </NavItem>
              </ShowFor>

              <ShowFor roles={['ADMIN_ROLE']}>
                <NavItem
                  to="/asset-manager"
                  icon={<Image className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/asset-manager')}
                >
                  Asset Manager
                </NavItem>
              </ShowFor>

              <ShowFor permissions={['INBOX_MANAGE']}>
                <NavItem
                  to="/engagement-stats"
                  icon={<Headset className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/engagement-stats')}
                >
                  Engagement Stats
                </NavItem>
              </ShowFor>

              <ShowFor permissions={['INBOX_MANAGE']}>
                <NavItem
                  to="/inbox-management"
                  icon={<Mailbox className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/inbox-management')}
                >
                  Inbox Management
                </NavItem>
              </ShowFor>

              <ShowFor permissions={['NUMBER_READ']}>
                <NavItem
                  to="/phone-numbers"
                  icon={<Phone className="size-5" />}
                  menuExpanded={isExpanded}
                  active={isActivePath(pathname, '/phone-numbers')}
                >
                  Numbers
                </NavItem>
              </ShowFor>
            </NavGroup>
          )}

          <ShowFor roles={['ADMIN_ROLE']}>
            <NavGroup title="System" menuExpanded={isExpanded}>
              <NavItem
                to="/admin/cache"
                icon={<Database className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/admin/cache')}
              >
                Cache
              </NavItem>
            </NavGroup>
          </ShowFor>

          <ShowFor roles={['ADMIN_ROLE']}>
            <NavGroup title="Toast Settings" menuExpanded={isExpanded}>
              <NavItem
                to="/settings/toast-guid-settings"
                icon={<SlidersHorizontal className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/settings/toast-guid-settings')}
              >
                Settings
              </NavItem>
              <NavItem
                to="/settings/toast-guids"
                icon={<Hash className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/settings/toast-guids')}
              >
                GUIDs
              </NavItem>
              <NavItem
                to="/settings/toast-guid-groups"
                icon={<FolderOpen className="size-5" />}
                menuExpanded={isExpanded}
                active={isActivePath(pathname, '/settings/toast-guid-groups')}
              >
                GUID Groups
              </NavItem>
            </NavGroup>
          </ShowFor>
        </nav>
        <div
          className={cn(
            'mt-auto flex cursor-default items-center gap-0.5 overflow-hidden p-4 text-sm text-nowrap text-zinc-500 transition-all duration-200',
            isExpanded ? 'opacity-100' : 'opacity-0',
          )}
        >
          <span>powered by</span>
          <img
            src="/aoh-logomark.png"
            alt="Art of Hospitality Solutions"
            className="inline h-5 w-auto pb-0.5"
          />
        </div>
      </aside>
    </div>
  );
}
